/**
 * SpinnerFullPage - Full viewport loading overlay
 *
 * Fixed-position full-page loading spinner with dark background.
 * Used for blocking operations that require full user attention.
 *
 * WHY fixed position:
 * - Prevents scroll during critical loading operations
 * - Ensures spinner is always visible above content
 * - Blocks interaction with underlying UI
 *
 * WHY different from SpinnerMDL:
 * - SpinnerMDL: Brand logo, app initialization, route transitions
 * - SpinnerFullPage: Generic loading, data fetching, form submissions
 *
 * Accessibility:
 * - data-testid for testing hooks
 * - Centered layout ensures screen reader focus
 * - Spinner component provides role="status"
 *
 * @see src/ui/Spinner.tsx - Base spinner component
 * @see src/ui/SpinnerMDL.tsx - Branded variant
 * @see src/ui/SpinnerFullElement.tsx - Element-scoped variant
 */
import { styled } from 'styled-components';
import Spinner from './Spinner';

/**
 * Fixed full-viewport container with dark overlay
 *
 * WHY fixed + 100vh/100vw:
 * - Covers entire viewport regardless of scroll position
 * - Blocks all interaction with underlying content
 * - Dark background prevents distraction
 *
 * WHY z-index 9999:
 * - Reduced from 99999 to prevent layering artifacts
 * - Still ensures display above normal content
 * - Below only critical system modals (z-index 10000+)
 */
const StyledSpinnerFullPage = styled.div`
  height: 100vh;
  width: 100vw;
  background-color: #111;
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 9999;
  // WHY: Reduced z-index from 99999 to 9999 to prevent layering artifacts
  // while still ensuring spinner displays above all normal content
`;

/**
 * SpinnerFullPage component
 *
 * WHY function component:
 * - No props needed (full-page loading is binary state)
 * - Consistent predictable experience
 * - Simplicity over configurability
 */
function SpinnerFullPage(): JSX.Element {
  return (
    <StyledSpinnerFullPage data-testid="spinner-fullpage">
      <Spinner />
    </StyledSpinnerFullPage>
  );
}

export default SpinnerFullPage;
